import React, { useEffect, useRef, useCallback } from 'react';
import { useNavStep } from '../../lib/NavHistory';
import { X, ChevronLeft } from 'lucide-react';

/**
 * ⛔⛔ THE ONE DRILL-DOWN POP-UP — Card P167.
 *
 * Daniel's ruling, 29 Oct: everything on the CEO dashboard opens the same
 * way. Before this there were TWO mechanisms — #742's modal for the headline
 * tiles, and #745's inline panels that scrolled into view for the rep
 * report, exceptions, finance and group-by — so the same gesture produced
 * two different experiences depending on which number you clicked.
 *
 * ⚠⚠ AND THE INLINE ONE HAD A REAL FAULT, not just an inconsistency: the
 * panel rendered BELOW a full-height screen, so a click low on the page
 * opened something a screen-height away. `scrollIntoView` papered over it by
 * moving the page, which leaves the answer's position depending on where the
 * question was asked. A modal is in the same place every time.
 *
 * ── Navigation stacks ──────────────────────────────────────────────────────
 * group view → View rows → one record. Back walks the stack, the title is a
 * breadcrumb, and Close dismisses the whole thing.
 *
 * ⚠⚠ P179 item 2 — THE BROWSER BACK BUTTON NOW WALKS THE STACK TOO, and that
 * reverses the note this comment used to carry ("Back is NOT the browser back
 * button … hijacking history would strand anyone who used it to leave the
 * page"). The concern was right and the conclusion was too broad: the fix is
 * not to own history directly but to register with `useNavStep`, the app-wide
 * step stack ContactDrawer already uses, which is exactly what stops a user
 * being stranded — it pops one step and hands the next Back to whoever owns it.
 *
 * ⛔ ONE STEP PER FRAME, not one for the whole dialog. With a single step,
 * Back from a record would close the entire modal rather than returning to the
 * list — the same dead end this card is fixing, moved to a different button.
 */

/** Everything focusable, in tab order. */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function DrilldownModal({
  stack = [], onBack, onClose, sessionNonce = '', children,
  /*
   * ⛔⛔ CARD P229 — OPT-IN, AND DEFAULTING TO THE BEHAVIOUR EVERY OTHER
   * DRILL-DOWN ALREADY HAS. A click outside closes this modal (the backdrop's
   * onMouseDown below), which is right for a figure drill-down you opened by
   * accident and wrong for a report that took a minute of the model's time to
   * write: Daniel lost one that way, and Print and Download PDF went with it.
   *
   * ⚠ Scoped by a prop rather than changed globally. Every tile drill-down on
   * the CEO dashboard shares this component, and making them all sticky to
   * fix one surface would be a worse trade than the bug.
   */
  closeOnBackdrop = true,
  /*
   * ⛔⛔ AND Esc IS BLOCKED WHILE A REPORT IS BEING WRITTEN. Separate from the
   * backdrop on purpose: a finished report can be dismissed with Esc like any
   * dialog, but a GENERATING one cannot — the model is a minute into writing
   * it, the dialog is the only thing showing that, and a stray Escape throws
   * away the one surface that says so. (The job itself survives — P219 keeps
   * it outside the dialog — but the reader has no way of knowing that mid-run.)
   */
  closeOnEsc = true,
}) {
  const panelRef = useRef(null);
  const openerRef = useRef(null);
  const open = stack.length > 0;

  /*
   * ⚠ The LABEL is generic. It appears in navigation UI, and a frame title can
   * be a group name; "Record" is the deepest it ever gets, never a person.
   */
  useNavStep({
    active: open,
    label: 'Drill-down',
    /*
     * ⛔⛔ WITHOUT THIS THE MODAL PUSHES A NEW HISTORY ENTRY EVERY TIME IT
     * OPENS, AND CAN NEVER REUSE ONE (Card P188).
     *
     * useBrowserBackStep adopts an existing marker only when it can prove
     * ownership — `mk.s === sessionNonce` — and its own docblock states the
     * consequence: "An absent nonce adopts nothing — a hook that cannot prove
     * ownership pushes its own entry instead." This call site passed none, so
     * open → close → reopen left TWO orphaned entries behind.
     *
     * ⚠ THAT IS THE BUG COWORK REPRODUCED. After closing and reopening the
     * drill-down (step d), the stale entry was still on the stack; the next
     * traversal landed on it, popstate fired, and this hook's onBack saw
     * stack.length === 1 and called onClose — so the whole modal vanished
     * instead of a record opening. Measured in
     * tests/react/drilldownNavStepRace.test.js: with a nonce the reopen ADOPTS
     * the same entry id; without one it mints a second.
     *
     * ⚠ App.js:944 already passes sessionNonce to every page; CeoDashboardPage
     * simply never forwarded it. 21 of 23 call sites across the tree are still
     * in this state — reported, not fixed here.
     */
    sessionNonce,
    onBack: () => {
      // ⛔ Walk one frame, and only close when this was the last one — so the
      // browser Back and the dialog's own Back do the same thing.
      if (stack.length > 1) { if (onBack) onBack(); } else if (onClose) onClose();
    },
  });

  /*
   * ⛔⛔ REMEMBER WHO OPENED IT, BEFORE focus moves. Captured on the
   * transition to open — reading document.activeElement later returns
   * something inside the dialog, and "focus returns to the button that
   * opened it" is then a no-op that still passes a naive test.
   */
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = (typeof document !== 'undefined' && document.activeElement) || null;
    return undefined;
  }, [open]);

  /* Move focus in. */
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const first = panelRef.current.querySelector(FOCUSABLE);
    (first || panelRef.current).focus({ preventScroll: true });
  }, [open, stack.length]);

  /*
   * ⚠ THE PAGE BEHIND MUST NOT SCROLL. Without this a wheel over the
   * backdrop scrolls the dashboard underneath, so dismissing the dialog
   * returns you somewhere you did not leave.
   */
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /** Close, returning focus to the opener. */
  const close = useCallback(() => {
    const opener = openerRef.current;
    if (typeof onClose === 'function') onClose();
    /*
     * ⚠ After the close, not before: restoring focus first lets React's
     * unmount move it again, and the caller ends up on <body>.
     */
    if (opener && typeof opener.focus === 'function') {
      window.setTimeout(() => { try { opener.focus(); } catch (e) { /* gone from the DOM */ } }, 0);
    }
  }, [onClose]);

  /* Esc closes; Tab is trapped. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        /* ⚠ Esc is kept when only the BACKDROP is sticky: a keyboard user
           needs a way out, and pressing Escape is deliberate in a way that
           clicking past a dialog is not. It is withheld only while a report
           is actually generating — the × is the way out then. */
        if (closeOnEsc) close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)]
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      /* ⛔ Wrap at both ends: a trap that only catches Tab lets Shift+Tab
         walk out of the back of the dialog, which is the half everyone
         forgets and a keyboard user finds immediately. */
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close, closeOnEsc]);

  if (!open) return null;

  const top = stack[stack.length - 1] || {};
  const crumb = stack.map((f) => f.title).filter(Boolean);

  return (
    <div
      data-testid="drilldown-modal"
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) close();
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={top.title || 'Figure detail'}
        tabIndex={-1}
        style={{
          background: '#fff', borderRadius: 12, width: '100%',
          /* ⚠ 1024px is the iPad landscape and the small-laptop width this
             has to read at; the panel is capped and scrolls inside rather
             than pushing the page. */
          maxWidth: 1040, maxHeight: 'calc(100vh - 80px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,.3)', outline: 'none',
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
          borderBottom: '1px solid #E3E7EE', flex: '0 0 auto',
        }}>
          {/* ⚠ `drilldown-modal-back`, not `drilldown-back`: MetricDrilldown
              already owns that id for its own group↔rows toggle. Two Backs
              under one id is a test that can pass on the wrong button —
              which is exactly how the first version of this arm read a
              child's control and reported the shell's stack as working. */}
          {stack.length > 1 ? (
            <button
              type="button" data-testid="drilldown-modal-back" onClick={onBack}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #E3E7EE',
                       background: '#fff', borderRadius: 6, padding: '4px 9px', fontSize: 12,
                       cursor: 'pointer', color: '#4B5563' }}
            >
              <ChevronLeft size={13} aria-hidden="true" /> Back
            </button>
          ) : null}

          {/* ⚠ The breadcrumb is the human title at every level — never a
              metric key. #745's metricTitle guard is what supplies it. */}
          <h2 data-testid="drilldown-title"
              style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: '#374151', minWidth: 0 }}>
            {crumb.join('  ›  ')}
          </h2>

          <button
            type="button" data-testid="drilldown-close" onClick={close} aria-label="Close"
            style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer',
                     color: '#6B7280', padding: 4, lineHeight: 0 }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div style={{ overflowY: 'auto', flex: '1 1 auto', padding: 8 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
